export interface AudioLevelDetectorConfig {
  rmsThreshold: number; // RMS amplitude threshold (0-1)
  rmsDbThreshold: number; // RMS in decibels threshold (e.g. -50)
  voiceEnergyThreshold: number; // Voice frequency band energy threshold
  silentDurationThreshold: number; // Time in ms before considering recording as silent
  sampleRate: number; // How often to check audio levels (ms)
}

export class AudioLevelDetector {
  private analyser: AnalyserNode;

  private dataArray: Float32Array;

  private isDetecting: boolean = false;

  private intervalId: ReturnType<typeof setInterval> | null = null;

  private silentStartTime: number | null = null;

  private config: AudioLevelDetectorConfig;

  private onSilentRecording: () => void;

  private onAudioDetected: () => void;

  private onUpdate: (level: number) => void;

  constructor(
    analyser: AnalyserNode,
    config: AudioLevelDetectorConfig,
    onSilentRecording: () => void,
    onAudioDetected: () => void,
    onUpdate: (level: number) => void,
  ) {
    this.analyser = analyser;
    this.config = config;
    this.onSilentRecording = onSilentRecording;
    this.onAudioDetected = onAudioDetected;
    this.onUpdate = onUpdate;
    this.dataArray = new Float32Array(analyser.fftSize);
  }

  start(): void {
    if (this.isDetecting) return;

    this.isDetecting = true;
    this.silentStartTime = Date.now();

    this.intervalId = setInterval(() => {
      this.checkAudioLevel();
    }, this.config.sampleRate);
  }

  stop(): void {
    if (!this.isDetecting) return;

    this.isDetecting = false;
    if (this.intervalId !== null) {
      clearInterval(this.intervalId);
      this.intervalId = null;
    }
    this.silentStartTime = null;
  }

  private checkAudioLevel(): void {
    if (!this.analyser || !this.dataArray) return;

    try {
      this.analyser.getFloatTimeDomainData(this.dataArray);

      // Calculate RMS (Root Mean Square) for amplitude
      let sumSquares = 0;
      for (let i = 0; i < this.dataArray.length; i += 1) {
        sumSquares += this.dataArray[i] * this.dataArray[i];
      }
      const rms = Math.sqrt(sumSquares / this.dataArray.length);

      // Calculate RMS in decibels
      const rmsDb = 20 * Math.log10(Math.max(rms, 1e-10));

      // Calculate voice energy using frequency data
      const freqData = new Uint8Array(this.analyser.frequencyBinCount);
      this.analyser.getByteFrequencyData(freqData);

      // Focus on voice frequency range (roughly 85Hz - 3000Hz)
      const { sampleRate } = this.analyser.context;
      const binSize = sampleRate / this.analyser.fftSize;
      const lowBin = Math.floor(85 / binSize);
      const highBin = Math.min(Math.ceil(3000 / binSize), freqData.length - 1);

      let voiceEnergy = 0;
      let voiceBins = 0;
      for (let i = lowBin; i <= highBin; i += 1) {
        voiceEnergy += freqData[i] / 255;
        voiceBins += 1;
      }
      voiceEnergy = voiceBins > 0 ? voiceEnergy / voiceBins : 0;

      this.onUpdate(rms);

      const hasAudio =
        rms > this.config.rmsThreshold ||
        rmsDb > this.config.rmsDbThreshold ||
        voiceEnergy > this.config.voiceEnergyThreshold;

      if (hasAudio) {
        this.silentStartTime = null;
        this.onAudioDetected();
      } else if (this.silentStartTime === null) {
        this.silentStartTime = Date.now();
      } else {
        const silentDuration = Date.now() - this.silentStartTime;
        if (silentDuration > this.config.silentDurationThreshold) {
          this.onSilentRecording();
          this.silentStartTime = Date.now();
        }
      }
    } catch (error) {
      console.error('Error checking audio level:', error);
      this.stop();
    }
  }
}
